import React, { useState } from 'react';
import { auth, db } from '../../../services/firebase';
import { addDoc, collection, serverTimestamp, setDoc, doc } from 'firebase/firestore';
import useStockStore from '../../../stores/useStockStore';
import { useActiveBroker } from '../../../hooks/useActiveBroker';
import { useNotifications } from '../../../contexts/NotificationContext';

const EditStockModal = ({ stock, onClose }) => {
  const { updateStockStatus } = useStockStore();
  const { notify } = useNotifications();
  const { brokerName } = useActiveBroker();

  const [isSubmitting, setIsSubmitting] = useState(false);
  // Debug logging removed to prevent infinite re-renders

  if (!stock) {
    // Debug logging removed to prevent infinite re-renders
    return null;
  }

  const handleStatusChange = async (newStatus) => {
    if (isSubmitting) {
      console.log('⏳ Prevent duplicate status change');
      return;
    }
    setIsSubmitting(true);
    // Debug logging removed to prevent infinite re-renders
    
    try {
      // Update stock status in store
      await updateStockStatus(stock.symbol, newStatus, stock.hedge);
      console.log('✅ Stock status updated successfully');
      
      // Always log action to both tbl_engagement and tbl_notifications
      try {
        const previousStatus = stock.status || 'No Status';
        
        // Get current user's account info
        const currentUser = auth.currentUser;
        const accountName = currentUser?.displayName || currentUser?.email || 'Unknown User';

        // Create log message based on new status
        let engagementLog = '';
        if (newStatus === 'Active') {
          engagementLog = `Active: ${stock.symbol}`;
        } else if (newStatus === 'Paused') {
          engagementLog = `Pause: ${stock.symbol}`;
        } else if (newStatus === 'Pending') {
          engagementLog = `Active: ${stock.symbol}`;
        } else if (newStatus === 'Exiting') {
          engagementLog = `Exit: ${stock.symbol}`;
        } else {
          engagementLog = `${newStatus}: ${stock.symbol}`;
        }

        // Upsert one engagement doc per user+symbol
        await setDoc(
          doc(db, 'tbl_engagement', `${accountName}_${stock.symbol}`),
          {
            username: accountName,
            userId: currentUser?.uid || 'anonymous',
            broker: brokerName || 'Unknown',
            symbol: stock.symbol,
            status: newStatus.toLowerCase(),
            maxPerTrade: stock.quanlity || 0,
            timestamp: serverTimestamp()
          },
          { merge: true }
        );
        console.log('✅ Action logged to tbl_engagement:', engagementLog);

        // Also log to tbl_notifications (idempotent per click)
        const actionId = `${currentUser?.uid || 'unknown'}_${stock.symbol}_${newStatus.toLowerCase()}_${Date.now()}`;
        await setDoc(doc(db, 'tbl_notifications', actionId), {
          log: engagementLog,
          timestamp: serverTimestamp(),
          userId: currentUser?.uid || 'unknown',
          symbol: stock.symbol,
          status: newStatus,
          action: newStatus,
          stockStatus: newStatus,
          previousStatus: previousStatus,
          stockType: stock.type,
          stockQuanlity: stock.quanlity,
          stockValue: stock.value,
          accountName: accountName,
          type: 'status_change',
          actionId
        });
        console.log('✅ Action also logged to tbl_notifications:', engagementLog);
      } catch (engagementError) {
        console.error('❌ Failed to log action:', engagementError);
      }
      
      onClose();
    } catch (error) {
      console.error('❌ Failed to update stock status:', error);
    }
    setIsSubmitting(false);
  };

      // Determine which buttons should be enabled based on current status
    const getButtonStates = () => {
      const currentStatus = stock.status?.toLowerCase();
      
      if (currentStatus === 'engage' || currentStatus === 'active') {
        return {
          engage: false,   // Disable Engage button (already engaged/Active)
          pause: true,     // Enable Pause button
          exit: false      // Disable Exit button (already engaged/Active)
        };
      } else if (currentStatus === 'pause' || currentStatus === 'paused') {
        return {
          engage: true,    // Enable Engage button
          pause: false,    // Disable Pause button (already paused)
          exit: false      // Disable Exit button (already paused)
        };
      } else if (currentStatus === 'pending') {
        return {
          engage: false,   // Disable Engage button (already Active)
          pause: false,    // Disable Pause button
          exit: false      // Disable Exit button (already Active)
        };
      } else if (currentStatus === 'filled') {
        return {
          engage: false,   // Disable Engage button (already filled)
          pause: false,    // Disable Pause button (already filled)
          exit: false      // Disable Exit button
        };
      } else {
        // Default: enable all buttons for unknown status
        return {
          engage: true,
          pause: true,
          exit: true
        };
      }
    };

  const buttonStates = getButtonStates();

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'active':
        return 'text-green-500';
      case 'filled':
        return 'text-blue-500';
      case 'pause':
      case 'paused':
      case 'exiting':
      case 'remove':
        return 'text-red-500';
      case 'pending':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999]">
      <div className="bg-gray-800 p-6 rounded-xl w-full max-w-sm text-white">
        <h3 className="text-xl font-bold mb-4">{stock.symbol}</h3>
        
        <div className="mb-4">
          <p className="text-gray-300 mb-2">Current Status:</p>
          <p className={`text-lg font-semibold ${getStatusColor(stock.status)}`}>{stock.status || 'No Status'}</p>
        </div>

        <div className="mb-6">
          <p className="text-gray-300 mb-3">Change Status:</p>
          <div className="space-y-2">
            <button
              onClick={() => handleStatusChange('Active')}
              disabled={!buttonStates.engage || isSubmitting}
              className={`w-full p-3 text-white rounded font-medium ${
                buttonStates.engage && !isSubmitting
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-gray-500 cursor-not-allowed opacity-50'
              }`}
            >
              Engage (Active)
            </button>
            <button
              onClick={() => handleStatusChange('Paused')}
              disabled={!buttonStates.pause || isSubmitting}
              className={`w-full p-3 text-white rounded font-medium ${
                buttonStates.pause && !isSubmitting
                  ? 'bg-yellow-600 hover:bg-yellow-700' 
                  : 'bg-gray-500 cursor-not-allowed opacity-50'
              }`}
            >
              Pause
            </button>
            <button
              onClick={() => handleStatusChange('Exiting')}
              disabled={!buttonStates.exit || isSubmitting}
              className={`w-full p-3 text-white rounded font-medium ${
                buttonStates.exit && !isSubmitting
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-gray-500 cursor-not-allowed opacity-50'
              }`}
            >
              Exit
            </button>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full p-3 bg-gray-600 hover:bg-gray-700 text-white rounded font-medium"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default EditStockModal;
